import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import CustomModal from "../../../../components/custom-modal/custom-modal";
import PdfViewer from "../../../../components/pdf-viewer";
import instance from "../../../../store/axios";
import { LOADING } from "../../../../store/actions";

interface ClosureDetailsModalProps {
  isVisible: boolean;
  closureDetails: any;
  onClose: () => void;
}

interface PdfViewerModalProps {
  isVisible: boolean;
  pdfUrl: string;
  onClose: () => void;
}

// Hook to manage closure details fetching
export const useClosureDetails = () => {
  const dispatch = useDispatch();
  const [closureDetails, setClosureDetails] = useState<any>(null);
  const [viewDetailsModal, setViewDetailsModal] = useState<boolean>(false);

  const fetchClosureDetails = async (requestId: number): Promise<void> => {
    dispatch({ type: LOADING, payload: true });
    try {
      const response = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/account-closure/${requestId}`
      );
      setClosureDetails(response.data);
      setViewDetailsModal(true);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
    dispatch({ type: LOADING, payload: false });
  };

  const closeDetailsModal = () => {
    setViewDetailsModal(false);
    setClosureDetails(null);
  };

  return {
    closureDetails,
    viewDetailsModal,
    fetchClosureDetails,
    closeDetailsModal,
  };
};

// Hook to manage PDF viewer
export const usePdfViewer = () => {
  const [pdfViewerModal, setPdfViewerModal] = useState<boolean>(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>("");

  const openPdfViewer = (url: string) => {
    setSelectedPdfUrl(url);
    setPdfViewerModal(true);
  };

  const closePdfViewer = () => {
    setPdfViewerModal(false);
    setSelectedPdfUrl("");
  };

  return {
    pdfViewerModal,
    selectedPdfUrl,
    openPdfViewer,
    closePdfViewer,
  };
};

// Closure Details Modal Component
export const ClosureDetailsModal: React.FC<ClosureDetailsModalProps> = ({
  isVisible,
  closureDetails,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { pdfViewerModal, selectedPdfUrl, openPdfViewer, closePdfViewer } =
    usePdfViewer();

  const fetchClosureForm = async (portfolioId: number) => {
    dispatch({ type: LOADING, payload: true });
    try {
      const response = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/portfolio/${portfolioId}/closure-request-form`
      );
      const base64Data = response.data?.data || response.data;
      if (base64Data) {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        openPdfViewer(blobUrl);
      }
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
    dispatch({ type: LOADING, payload: false });
  };

  return (
    <>
      <CustomModal
        isVisible={isVisible}
        centered
        width={700}
        showClose={true}
        onClose={onClose}
      >
        <div className="flex flex-col p-4">
          <h3 className="text-xl font-semibold mb-4 text-blue-500">
            Closure Request Details
          </h3>

          {closureDetails && (
            <>
              {/* Client Info */}
              <div className="mb-4">
                <div className="flex gap-3 items-center mb-3">
                  <span className="w-[4px] bg-blue-500 h-[20px] rounded-[10px]"></span>
                  <h4 className="text-base font-semibold">Client Information</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 pl-4">
                  <div>
                    <p className="text-gray-500 text-sm">Name</p>
                    <p className="font-medium">
                      {closureDetails.user?.firstName}{" "}
                      {closureDetails.user?.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Email</p>
                    <p className="font-medium">{closureDetails.user?.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Account Code</p>
                    <p className="font-medium">
                      {closureDetails.account?.accountCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Status</p>
                    <p className="font-medium">{closureDetails.status}</p>
                  </div>
                </div>
              </div>

              {/* Closure Reasons */}
              <div className="mb-4">
                <div className="flex gap-3 items-center mb-3">
                  <span className="w-[4px] bg-blue-500 h-[20px] rounded-[10px]"></span>
                  <h4 className="text-base font-semibold">Closure Reasons</h4>
                </div>
                <div className="pl-4">
                  {closureDetails.closureReasons?.map(
                    (reason: any, index: number) => (
                      <div key={index} className="mb-2 p-2 bg-gray-50 rounded-lg">
                        <p className="text-gray-500 text-xs">
                          {reason.reasonCategory}
                        </p>
                        <p className="font-medium text-sm">{reason.reason}</p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Feedback */}
              {closureDetails.feedback && (
                <div className="mb-4">
                  <div className="flex gap-3 items-center mb-3">
                    <span className="w-[4px] bg-blue-500 h-[20px] rounded-[10px]"></span>
                    <h4 className="text-base font-semibold">Feedback</h4>
                  </div>
                  <p className="pl-4 text-gray-700">{closureDetails.feedback}</p>
                </div>
              )}

              {/* Portfolios */}
              {closureDetails.account?.portfolios?.length > 0 && (
                <div className="mb-4">
                  <div className="flex gap-3 items-center mb-3">
                    <span className="w-[4px] bg-blue-500 h-[20px] rounded-[10px]"></span>
                    <h4 className="text-base font-semibold">
                      Portfolios Closure Forms
                    </h4>
                  </div>
                  <div className="pl-4 grid grid-cols-1 gap-3">
                    {closureDetails.account.portfolios.map((portfolio: any) => (
                      <div
                        key={portfolio.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div>
                          <p className="text-gray-500 text-xs">Portfolio number</p>
                          <p className="font-semibold">{portfolio.accountCode}</p>
                        </div>
                        {(portfolio.closureForm || closureDetails.status === "Pending") && (
                          <button
                            onClick={() => {
                              if (closureDetails.status === "Pending") {
                                fetchClosureForm(portfolio.id);
                              } else {
                                openPdfViewer(portfolio.closureForm);
                              }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            View Closure Form
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CustomModal>

      {/* PDF Viewer Modal */}
      <PdfViewerModal
        isVisible={pdfViewerModal}
        pdfUrl={selectedPdfUrl}
        onClose={closePdfViewer}
      />
    </>
  );
};

// PDF Viewer Modal Component
export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  isVisible,
  pdfUrl,
  onClose,
}) => {
  return (
    <CustomModal
      isVisible={isVisible}
      centered
      width={1100}
      showClose={true}
      onClose={onClose}
      className="pdf-viewer-modal"
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{ height: "80vh" }}
      >
        {pdfUrl && <PdfViewer fileUrl={pdfUrl} />}
      </div>
    </CustomModal>
  );
};

export default ClosureDetailsModal;
